import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { GripVertical } from "lucide-react";

function SortableRow({ id, renderRow, dragHandleTestid }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 10 : "auto",
    position: "relative",
  };
  const handle = (
    <button
      type="button"
      ref={setNodeRef === undefined ? undefined : undefined}
      {...attributes}
      {...listeners}
      data-testid={dragHandleTestid}
      className="cursor-grab active:cursor-grabbing touch-none p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary/60"
      aria-label="Drag to reorder"
    >
      <GripVertical className="w-4 h-4" />
    </button>
  );
  return (
    <div ref={setNodeRef} style={style}>
      {renderRow(handle)}
    </div>
  );
}

/**
 * Sortable list with drag-and-drop reordering.
 *
 * Props:
 *  - items: array of objects, each with a stable `id` field
 *  - onReorder(newItems): called with the new array after a successful drag (id order)
 *  - renderItem(item, handle): function returning the JSX for one row. `handle` is the drag handle JSX you can place anywhere in the row.
 *  - dragHandleTestid(item): optional fn returning the data-testid for that item's drag handle
 */
export default function SortableList({ items, onReorder, renderItem, dragHandleTestid }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const handleDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = items.findIndex((i) => i.id === active.id);
    const newIndex = items.findIndex((i) => i.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;
    const newArr = arrayMove(items, oldIndex, newIndex);
    onReorder(newArr);
  };

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map((i) => i.id)} strategy={verticalListSortingStrategy}>
        <div>
          {items.map((item) => (
            <SortableRow
              key={item.id}
              id={item.id}
              dragHandleTestid={dragHandleTestid ? dragHandleTestid(item) : undefined}
              renderRow={(handle) => renderItem(item, handle)}
            />
          ))}
        </div>
      </SortableContext>
    </DndContext>
  );
}
